/**
 * Repository Index
 *
 * Exports the repository interface and factory function.
 */

import type { Family } from '@/types/database'
import type { IStoryRepository } from './types'
import { localStoryRepository, setCurrentChild } from './localStoryRepository'
import { cloudStoryRepository, setReadOnlyMode } from './cloudStoryRepository'
import { shouldUseCloudRepository, isReadOnly } from '@/services/entitlementsService'

// Re-export types and interfaces
export type { IStoryRepository, StoryUpdates } from './types'
export type { StoryRecord, StepRecord, StoryWithSteps, RepositoryResult } from '@/types/domain'

// Re-export individual repositories for direct access if needed
export { localStoryRepository, cloudStoryRepository }

/**
 * Factory function to get the appropriate repository based on family subscription.
 *
 * Design:
 * - Premium tier (any status) → cloudStoryRepository (so they can see saved work)
 * - Free tier → localStoryRepository
 *
 * Also configures the repository's internal state (read-only mode, child context).
 */
export function getStoryRepository(
  family: Family | null,
  childId: string | null
): IStoryRepository {
  // Configure child context for local repo
  setCurrentChild(childId)

  // Check if we should use cloud repository
  const useCloud = shouldUseCloudRepository(family)

  if (useCloud) {
    // Configure read-only mode for lapsed subscriptions
    const readOnly = isReadOnly(family)
    setReadOnlyMode(readOnly)
    return cloudStoryRepository
  }

  return localStoryRepository
}

/**
 * Simpler factory that takes mode directly (for use when mode is already computed)
 */
export function getStoryRepositoryByMode(
  mode: 'local' | 'cloud',
  readOnly: boolean = false,
  childId: string | null = null
): IStoryRepository {
  setCurrentChild(childId)

  if (mode === 'cloud') {
    setReadOnlyMode(readOnly)
    return cloudStoryRepository
  }

  return localStoryRepository
}
